/**
 * Per-device "which announcements has this device already shown?" set. Mirrors the
 * tutorial/questionnaire seen flags (localStorage on web, AsyncStorage on native, with
 * an in-memory cache). Stored as a single JSON array of ids, pruned to the most recent
 * ANNOUNCEMENT_SEEN_MAX. See specs/in-app-announcements/spec.md (AC-1.5).
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from '../../helpers/logger.helper';
import {
  ANNOUNCEMENT_SEEN_STORAGE_KEY as KEY,
  ANNOUNCEMENT_SEEN_MAX as MAX,
} from '../constants/announcements.constants';

/** Ordered list of seen ids (oldest first); null until first load. */
let _cached: string[] | null = null;

/** Test helper — clear the in-memory cache between cases. */
export function resetAnnouncementSeenCache(): void {
  _cached = null;
}

function parse(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const value = JSON.parse(raw);
    return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

async function readRaw(): Promise<string | null> {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return localStorage.getItem(KEY);
  }
  return AsyncStorage.getItem(KEY);
}

async function writeRaw(value: string): Promise<void> {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    localStorage.setItem(KEY, value);
    return;
  }
  await AsyncStorage.setItem(KEY, value);
}

/** Ids this device has already shown. Reads once, then serves from memory. */
export async function getSeenAnnouncementIds(): Promise<string[]> {
  if (_cached !== null) return _cached;
  try {
    _cached = parse(await readRaw());
  } catch (error) {
    if (__DEV__) logger.warn('Failed to read seen announcements', error);
    _cached = [];
  }
  return _cached;
}

/** Record an announcement as shown on this device (idempotent; prunes to MAX). */
export async function markAnnouncementSeen(id: string): Promise<void> {
  if (!id) return;
  const current = await getSeenAnnouncementIds();
  if (current.includes(id)) return;
  const next = [...current, id].slice(-MAX);
  _cached = next; // update memory first so a same-session re-eval never re-shows
  try {
    await writeRaw(JSON.stringify(next));
  } catch (error) {
    if (__DEV__) logger.warn('Failed to persist seen announcement', error);
  }
}
